/*
 * Getters for the [board store]{link module:store/board}
 *
 * @module store/board/getters
 */

import { Group, Name } from '@/store/modules/board'
import { PERMISSION_FLAGS } from '@/store/modules/users'
import { PRODUCER_DEVICES } from '@/store/modules/measurements'

/**
 * Getter to get the current config
 *
 * @function
 *
 * @return {Group[]}
 */
export const config = (state) => () => state.config

/**
 * Getter to get the current config enhanced by the deviceClassification
 *
 * returns a config with deviceClassification, needed to present colors based on input devices
 * deviceClassification => { supply: <devClassType>, demand: <devClassType> }
 * devClassType = con|gen|grid|unspecified
 *
 * @function
 *
 * @return {Group[]}
 */
export const configForPresentation = (state, getters, rootState) => () =>
  enhanceConfigByDeviceClassification(state.config, rootState.measurements.sensors)

/**
 * Helperfunction to traverse the config and enhance each group
 */
const enhanceConfigByDeviceClassification = (gs, sensors) => {
  const enhancedGs = []
  for (const g of gs) {
    const enhancedG = enhanceGroupByDeviceClassification(g, sensors)
    enhancedG.children = enhanceConfigByDeviceClassification(g.children, sensors)
    enhancedGs.push(enhancedG)
  }
  return enhancedGs
}

/**
 * Helperfunction to enhance a group by deviceClassification flag
 */
const enhanceGroupByDeviceClassification = (g, sensors) => {
  const group = { ...g }
  for (const c of g.compRules) {
    if (c.type === 'power') {
      group.deviceClassification = getDeviceClassificationFromOperation(
        c.operation,
        sensors,
        g.deviceClassification,
        !!g.children.length
      )
    }
  }
  group.deviceClassification = group.deviceClassification ?? null

  return group
}

/**
 * Helperfunction to retrieve a deviceClassification based on the operation
 */
const getDeviceClassificationFromOperation = (op, sensors, init, hasChildren) => {
  let devClass = init
  if (op.operator === 'eval') {
    devClass = mergeDevClasses(devClass, parseDevClassWithEvalTree(op.tree, sensors, hasChildren))
  } else {
    for (const ref of op.references) {
      devClass = mergeDevClasses(devClass, getDeviceClassForSensor(sensors[ref], hasChildren))
    }
  }
  return devClass
}

/**
 * Helperfunction to traverse an EvalTree and calculate the deviceClassification
 */
const parseDevClassWithEvalTree = (tree, sensors, hasChildren) => {
  if (!tree) {
    return null
  }
  if (tree.value) {
    return null
  }
  if (tree.reference) {
    return getDeviceClassForSensor(sensors[tree.reference], hasChildren)
  }
  if (tree.operator) {
    return mergeDevClasses(
      parseDevClassWithEvalTree(tree.left, sensors, hasChildren),
      parseDevClassWithEvalTree(tree.right, sensors, hasChildren)
    )
  }
}

/**
 * Helperfunction to get the deviceClassification based on a sensor
 * if a grid node has Children => switch supply and demand
 * if not has Children => treat as normal leaf
 *
 */
const getDeviceClassForSensor = (sensor, hasChildren) => {
  if (!sensor) {
    return null
  }
  if (sensor.deviceType === 'grid') {
    if (hasChildren) {
      return { supply: 'con', demand: 'grid' }
    } else {
      return { supply: 'grid', demand: 'con' }
    }
  }

  if (sensor.deviceType === 'battery') {
    return { supply: 'gen', demand: 'gen' }
  }
  if (PRODUCER_DEVICES.includes(sensor.deviceType)) {
    return { supply: 'gen', demand: 'gen' }
  }
  return { supply: 'gen', demand: 'con' }
}

/**
 * Helperfunction to merge two given deviceClassification`s
 */
const mergeDevClasses = (class1, class2) => {
  if (!class1) {
    return class2
  }
  if (!class2) {
    return class1
  }
  if (class2.supply === class1.supply) {
    if (class2.demand === class1.demand) {
      // BOTH IDENTICAL
      return class2
    } else {
      // SUPPLY IDENTICAL, DEMAND DIFFERENT
      if (
        (class1.demand === 'gen' || class1.demand === 'con') &&
        (class2.demand === 'gen' || class2.demand === 'con')
      ) {
        return { supply: class2.supply, demand: 'con' }
      }
      return { supply: class2.supply, demand: 'unspecified' }
    }
  } else {
    if (class2.demand === class1.demand) {
      // SUPPLY DIFFERENT, DEMAND IDENTICAL
      return { supply: 'unspecified', demand: class2.demand }
    } else {
      // BOTH DIFFERENT
      if (
        (class1.demand === 'gen' || class1.demand === 'con') &&
        (class2.demand === 'gen' || class2.demand === 'con')
      ) {
        return { supply: 'unspecified', demand: 'con' }
      }
      return { supply: 'unspecified', demand: 'unspecified' }
    }
  }
}

/**
 * Getter to return a Config created based on a Community
 *
 * Creates a config with the group Tree
 *
 * @function
 *
 * @return {Group[]}
 */
export const communityConfig = (state, getters, rootState, rootGetters) => () =>
  createConfigTree(
    rootState.energyManagementSystemGroups.emsGroups.hierarchy,
    rootState.energyManagementSystemGroups.emsGroups.hash,
    false,
    rootGetters
  )

/**
 * Getter to return a Config created based on a Community with Permissions
 *
 * Creates a config with the group Tree
 *
 * @function
 *
 * @return {Group[]}
 */
export const communityConfigWithPermissions = (_state, _getters, rootState, rootGetters) => () =>
  createConfigTree(
    rootState.energyManagementSystemGroups.emsGroups.hierarchy,
    rootState.energyManagementSystemGroups.emsGroups.hash,
    true,
    rootGetters
  )

/**
 * Getter to return all Groups in a config as Select Options
 *
 * Ignores a Group if the id is given
 *
 * Creates a config with the group Tree
 *
 * @function
 *
 * @params {integer} gId, groupId to ignore
 * @params {String} locale, {'en', 'de'}
 *
 * @return {Object}Array {{String} value, {String} label}
 */
export const getAllConfigGroupsSelect = (state) => {
  return ({ gId = null, locale = 'en' }) => getTreeGroupsSelect(gId, state.config, locale)
}

/**
 *
 * Helper Function to create a community config
 * builds the config based on the given groupTree
 * adds permissionTag based on the permission of the dummy user
 *
 */
const createConfigTree = (tree, vals, withPermissions, rootGetters) => {
  const gs = []
  for (const id in tree) {
    const val = vals[id]
    const g = new Group({
      id,
      name: new Name({ en: val.name, de: val.name }),
      showChildren: true,
      tag: val.tag,
      permission: withPermissions
        ? translatePermissionToTag(rootGetters['users/getGroupPermissionForUser']({ userId: -1, groupId: id }))
        : null,
      children: createConfigTree(tree[id], vals, withPermissions, rootGetters)
    })
    gs.push(g)
  }
  return gs
}

/**
 *
 * Helper Function to Select Options from a Group Array
 *
 */
const getTreeGroupsSelect = (gId, gs, locale) => {
  let options = []
  for (const g of gs) {
    if (gId !== g.id) {
      options.push({
        value: g.id,
        label: g.name[locale]
      })
    }
    options = options.concat(getTreeGroupsSelect(gId, g.children, locale))
  }
  return options
}

/**
 *
 * Helper Function to translate a Permission into a tag
 *
 * @param {Permission} permission
 *
 * @return {String} 'hide'|'show'|'admin'
 *
 */
const translatePermissionToTag = (permission) => {
  const perm = permission['customer'] + permission['admin']

  if (perm & PERMISSION_FLAGS['read']) {
    if (perm & PERMISSION_FLAGS['orgRead'] && perm & PERMISSION_FLAGS['orgWrite']) {
      return 'admin'
    }
    return 'show'
  }
  return 'hide'
}
